import { Field, Form, Formik } from "formik";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import { bool } from "prop-types";
import React, { useEffect, useState } from "react";
import CustomText from "pages/_components/CustomText/CustomText";
import ResponsiveHeader from "pages/_components/header";
import Checkbox from "pages/_components/Checkbox";
import GridTable from "pages/_components/GridTable/GridTable";
import * as configUtils from "util/config";
import Selector from "pages/_components/fields/formik/Selector";
import * as i18n from "util/i18n";
import { selectors as sessionSelectors } from "reducers/session";
import { store } from "store";
import { Col, Row } from "react-bootstrap";
import Button from "pages/_components/Button";
import * as Yup from "yup";
import { push } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { actions as comexActions } from "reducers/comex";
import ComexStepper from "./_components/ComexStepper";

const Step1Comex = ({ isDesktop }) => {
    const FORM_ID = "comex.concept";
    const OTHER_CONCEPT = "OTHERS";

    const dispatch = useDispatch();
    const comexTransfer = useSelector((state) => state.comex.comexTransfer) || {};
    const [otherConceptsAvailable, setOtherConceptsAvailable] = useState(
        comexTransfer.selectedConcept === OTHER_CONCEPT,
    );

    const comexConcepts = configUtils.getArray("comex.concepts");
    const conceptGroupsConfig = configUtils.getArray("comex.concept.groups");
    const otherConceptsConfig = configUtils.getArray("comex.other.concepts");

    const conceptGroups = i18n.formatOptionLabel(conceptGroupsConfig, `${FORM_ID}.group`);
    const otherConcepts = i18n.formatOptionLabel(otherConceptsConfig, `${FORM_ID}.others`);

    const lastItem = comexConcepts.length + 1;
    const [filteredOtherConcepts, setFilteredOtherConcepts] = useState([]);
    const username = sessionSelectors.getUserFullName(store.getState());
    const title = i18n.get("comex.request.title").replace("#{NAME}", username);

    const handleCheckboxChange = (setFieldValue, selectedConcept) => {
        setFieldValue(selectedConcept, true);
        setOtherConceptsAvailable(selectedConcept === OTHER_CONCEPT);

        comexConcepts.forEach((concept) => {
            if (concept !== selectedConcept) {
                setFieldValue(concept, false);
            }
        });
    };

    const handleGroupChange = (value) => {
        const filteredOptions = otherConcepts.filter((concept) => concept.value.includes(value));
        setFilteredOtherConcepts(filteredOptions);
    };

    useEffect(() => {
        if (comexTransfer.selectedConcept === OTHER_CONCEPT && comexTransfer.group) {
            handleGroupChange(comexTransfer.group);
        }
    }, [comexTransfer.selectedConcept, comexTransfer.group]);

    const validationSchema = Yup.object().shape({
        group: Yup.string().when([], {
            is: () => otherConceptsAvailable,
            then: Yup.string().required(i18n.get("global.error.required")),
        }),
        otherConcepts: Yup.string().when([], {
            is: () => otherConceptsAvailable,
            then: Yup.string().required(i18n.get("global.error.required")),
        }),
    });

    const getInitialValues = () =>
        comexConcepts.reduce(
            (acc, concept) => ({
                ...acc,
                [concept]: comexTransfer.selectedConcept === concept, 
            }),
            {
                group: comexTransfer.selectedConcept === "OTHERS" ? comexTransfer.group || "" : "",
                otherConcepts: comexTransfer.selectedConcept === "OTHERS" ? comexTransfer.otherConcepts || "" : "",
            },
        );

    const initialValues = getInitialValues();

    const handleSubmit = (values) => {
        const selectedConcept =
            Object.keys(values).find((key) => key !== "group" && key !== "otherConcepts" && values[key]) || null;

        const concept = selectedConcept !== OTHER_CONCEPT ? selectedConcept : values.otherConcepts;

        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                concept,
                selectedConcept,
                group: selectedConcept === OTHER_CONCEPT ? values.group : "",
                otherConcepts: selectedConcept === OTHER_CONCEPT ? values.otherConcepts : "",
            }),
        );
        dispatch(push("/comex/payment"));
    };

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={1} />}
            <Notification scopeToShow="Step1Comex" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo="/comex/contact/add" />

            <MainContainer>
                {!isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={1} />}

                <h2>{title}</h2>
                <h3>
                    <CustomText text="comex.concept.message" textType="I18N" />
                </h3>
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={(values) => handleSubmit(values)}>
                    {({ values, handleChange, setFieldValue }) => {
                        const canSubmit =
                            Object.values(values).some((value) => value) &&
                            (!otherConceptsAvailable || (values.group && values.otherConcepts));

                        return (
                            <>
                                <Form onChange={handleChange}>
                                    <GridTable className="form">
                                        <GridTable.Container columns={12} columnsTemplate="fixed">
                                            {comexConcepts.map((concept, index) => (
                                                <GridTable.Data
                                                    columnStart={2}
                                                    columnWidth={10}
                                                    rowStart={index + 1}
                                                    key={concept}>
                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name={concept}
                                                        checked={values[concept] || false}
                                                        onChange={() => handleCheckboxChange(setFieldValue, concept)}
                                                        labelClassName="concepts-checkbox"
                                                        aditionalI18nText={
                                                            concept !== OTHER_CONCEPT
                                                                ? `comex.concept.${concept}.detail.label`
                                                                : null
                                                        }
                                                    />
                                                </GridTable.Data>
                                            ))}
                                            {otherConceptsAvailable && (
                                                <>
                                                    <GridTable.Data
                                                        className="required other-concept-combobox"
                                                        columnStart={2}
                                                        columnWidth={4}
                                                        rowStart={lastItem}>
                                                        <Field
                                                            component={Selector}
                                                            idForm={FORM_ID}
                                                            name="group"
                                                            options={conceptGroups}
                                                            placeholder=""
                                                            onChange={handleGroupChange}
                                                            value={values.group}
                                                            labelClassName="other-concept-label data-label"
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data
                                                        className="required other-concept-combobox"
                                                        columnStart={6}
                                                        columnWidth={6}
                                                        rowStart={lastItem}>
                                                        <Field
                                                            component={Selector}
                                                            idForm={FORM_ID}
                                                            name="otherConcepts"
                                                            options={filteredOtherConcepts}
                                                            placeholder=""
                                                            value={values.otherConcepts}
                                                            labelClassName="other-concept-label data-label"
                                                        />
                                                    </GridTable.Data>
                                                </>
                                            )}
                                        </GridTable.Container>
                                    </GridTable>
                                    <Row className="form-actions">
                                        <Col xs={12} md={4} mdOffset={4}>
                                            <Button
                                                type="submit"
                                                className="btn-primary"
                                                label="global.continue"
                                                disabled={!canSubmit}
                                            />
                                        </Col>
                                    </Row>
                                </Form>
                            </>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

export default Step1Comex;

Step1Comex.propTypes = {
    isDesktop: bool.isRequired,
};
